import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Target, Plus, Loader2, Building2, Phone, MapPin, DollarSign, Users, Search,
} from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { key: "new",        label: "New",         color: "bg-blue-500/10 border-blue-500/30 text-blue-300" },
  { key: "visited",    label: "Visited",     color: "bg-cyan-500/10 border-cyan-500/30 text-cyan-300" },
  { key: "qualified",  label: "Qualified",   color: "bg-amber-500/10 border-amber-500/30 text-amber-300" },
  { key: "quote_sent", label: "Quote Sent",  color: "bg-violet-500/10 border-violet-500/30 text-violet-300" },
  { key: "won",        label: "Won",         color: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" },
  { key: "lost",       label: "Lost",        color: "bg-rose-500/10 border-rose-500/30 text-rose-300" },
];
const TYPES = [
  { key: "body_shop",  label: "Body Shop" },
  { key: "glass_shop", label: "Glass Shop" },
  { key: "dealership", label: "Dealership" },
  { key: "fleet",      label: "Fleet" },
  { key: "insurance",  label: "Insurance" },
  { key: "other",      label: "Other" },
];

export default function SalesPipeline() {
  const navigate = useNavigate();
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [dragging, setDragging] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = search.trim() ? { search: search.trim() } : {};
      const r = await api.get("/sales/prospects", { params });
      setProspects(r.data);
    } catch { toast.error("Failed to load prospects"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  const grouped = useMemo(() => {
    const g = {};
    STATUSES.forEach((s) => { g[s.key] = []; });
    prospects.forEach((p) => { (g[p.status] = g[p.status] || []).push(p); });
    return g;
  }, [prospects]);

  const [wonPopup, setWonPopup] = useState(null); // {shop_id, name}

  const onDrop = async (status, e) => {
    e.preventDefault();
    if (!dragging) return;
    if (dragging.status === status) { setDragging(null); return; }
    setProspects((prev) => prev.map((p) => p.id === dragging.id ? { ...p, status } : p));
    try {
      const r = await api.patch(`/sales/prospects/${dragging.id}`, { status });
      toast.success(`Moved to "${STATUSES.find((s) => s.key === status)?.label}"`);
      // If moving to Won auto-created a body shop that needs discount setup → open modal
      const bs = r.data?.auto_created_body_shop;
      if (status === "won" && bs && bs.needs_discount) {
        setWonPopup(bs);
      }
    } catch {
      toast.error("Move failed — reloading");
      load();
    } finally { setDragging(null); }
  };

  return (
    <div className="space-y-5" data-testid="sales-pipeline-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Target className="w-7 h-7 text-emerald-400" /> Sales Pipeline
          </h1>
          <p className="text-slate-400 text-sm mt-1">Drag prospects between stages. Click a card to log a visit, call, email, or quote.</p>
        </div>
        <Button data-testid="prospect-new-btn" onClick={() => setCreateOpen(true)} className="bg-emerald-600 hover:bg-emerald-500">
          <Plus className="w-4 h-4 mr-2" /> Add Prospect
        </Button>
      </div>

      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-500" />
          <Input
            data-testid="prospect-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search company / contact / city..."
            className="pl-8 bg-[#030712] border-slate-800"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center p-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-400 mx-auto" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 overflow-x-auto">
          {STATUSES.map((s) => (
            <div
              key={s.key}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => onDrop(s.key, e)}
              className="rounded-xl border border-slate-800 bg-[#050a17] p-2.5 min-h-[400px]"
              data-testid={`pipeline-col-${s.key}`}
            >
              <div className="flex items-center justify-between mb-2.5 px-1">
                <Badge className={`${s.color} text-[10px] uppercase tracking-wider`}>{s.label}</Badge>
                <span className="text-xs text-slate-500">{grouped[s.key].length}</span>
              </div>
              <div className="space-y-2">
                {grouped[s.key].map((p) => (
                  <Link
                    key={p.id}
                    to={`/sales/prospects/${p.id}`}
                    draggable
                    onDragStart={() => setDragging(p)}
                    onDragEnd={() => setDragging(null)}
                    data-testid={`prospect-card-${p.id}`}
                    className="block rounded-lg border border-slate-800 bg-[#0B1120] p-3 hover:border-emerald-500/40 cursor-grab active:cursor-grabbing"
                  >
                    <div className="text-sm font-semibold text-slate-100 truncate flex items-center gap-1.5">
                      <Building2 className="w-3.5 h-3.5 text-slate-500 shrink-0" /> {p.company_name}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1.5">
                      <Badge className="bg-slate-800 border-slate-700 text-slate-300 text-[9px]">
                        {TYPES.find((t) => t.key === p.type)?.label || p.type}
                      </Badge>
                      {p.estimated_value ? (
                        <span className="text-emerald-400 font-medium flex items-center gap-0.5">
                          <DollarSign className="w-2.5 h-2.5" />{Number(p.estimated_value).toLocaleString()}
                        </span>
                      ) : null}
                    </div>
                    {p.contact_name && (
                      <div className="text-[11px] text-slate-400 mt-1.5 truncate">{p.contact_name}</div>
                    )}
                    {p.city && (
                      <div className="text-[10px] text-slate-500 mt-0.5 truncate flex items-center gap-0.5">
                        <MapPin className="w-2.5 h-2.5" /> {p.city}{p.state ? `, ${p.state}` : ""}
                      </div>
                    )}
                    <div className="text-[10px] text-slate-600 mt-1.5 flex items-center justify-between">
                      <span>{(p.activity_count || 0)} activities</span>
                      {p.phone && <Phone className="w-2.5 h-2.5" />}
                    </div>
                  </Link>
                ))}
                {grouped[s.key].length === 0 && (
                  <div className="text-[10px] text-slate-700 text-center italic py-4">Drop here</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <CreateProspectDialog
        key={`create-${createOpen ? "o" : "c"}`}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(p) => { setCreateOpen(false); navigate(`/sales/prospects/${p.id}`); }}
      />

      {wonPopup && (
        <WonDiscountDialog
          shop={wonPopup}
          onClose={() => setWonPopup(null)}
        />
      )}
    </div>
  );
}

function WonDiscountDialog({ shop, onClose }) {
  const [pct, setPct] = useState("25");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const val = parseFloat(pct);
    if (isNaN(val) || val < 0 || val > 90) {
      toast.error("Digite um valor entre 0 e 90");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/sales/body-shops/${shop.id}/discount`, { default_discount_pct: val });
      toast.success(`Desconto de ${val}% salvo para ${shop.name}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0B1120] border-emerald-500/40 max-w-md" data-testid="won-discount-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-300">
            🎉 Prospect ganho! Vamos configurar {shop.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <p className="text-sm text-slate-300 leading-relaxed">
            Um novo body shop foi criado automaticamente. Qual foi o <strong className="text-amber-300">desconto acordado</strong> com este cliente?
            Esse valor é aplicado nas cotações que ele solicitar pelo portal dele.
          </p>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-bold">Desconto acordado (%)</Label>
            <div className="mt-2 flex items-center gap-2">
              <Input
                type="number"
                min="0"
                max="90"
                step="1"
                value={pct}
                onChange={(e) => setPct(e.target.value)}
                data-testid="won-discount-input"
                className="bg-slate-950 border-slate-800 text-2xl font-mono text-center h-14 w-32"
              />
              <span className="text-2xl font-mono text-emerald-300">%</span>
            </div>
            <div className="flex gap-1 mt-2">
              {[10, 15, 20, 25, 30].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setPct(String(v))}
                  className="px-2 py-1 rounded border border-slate-700 text-[11px] text-slate-400 hover:border-emerald-500 hover:text-emerald-300"
                  data-testid={`won-preset-${v}`}
                >
                  {v}%
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-800">
            <Button variant="outline" onClick={onClose} className="border-slate-700">Depois</Button>
            <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500" data-testid="won-discount-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : "Salvar desconto"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CreateProspectDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    company_name: "", type: "body_shop", contact_name: "", phone: "", email: "",
    city: "", state: "", estimated_volume: "", estimated_value: "",
    source: "cold_visit", notes: "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.company_name.trim()) { toast.error("Company name is required"); return; }
    setSaving(true);
    try {
      const body = {
        ...form,
        estimated_volume: form.estimated_volume ? parseInt(form.estimated_volume, 10) : null,
        estimated_value: form.estimated_value ? parseFloat(form.estimated_value) : null,
      };
      const r = await api.post("/sales/prospects", body);
      toast.success(`Prospect "${r.data.company_name}" created`);
      onCreated?.(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-[#0B1120] border-slate-800 max-h-[90vh] overflow-y-auto" data-testid="prospect-create-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="w-4 h-4 text-emerald-400" /> New Prospect</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">Add a body shop, dealership, or fleet to start tracking the relationship.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field label="Company Name *" testId="pf-company">
            <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
              data-testid="pf-input-company" className="bg-[#030712] border-slate-800" placeholder="ABC Body Shop" />
          </Field>
          <Field label="Type" testId="pf-type">
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
              data-testid="pf-input-type"
              className="h-10 w-full px-3 rounded-md bg-[#030712] border border-slate-800 text-sm text-slate-200">
              {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Contact Name" testId="pf-contact"><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="bg-[#030712] border-slate-800" /></Field>
          <Field label="Phone" testId="pf-phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#030712] border-slate-800" /></Field>
          <Field label="Email" testId="pf-email" span2><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#030712] border-slate-800" /></Field>
          <Field label="City" testId="pf-city"><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-[#030712] border-slate-800" /></Field>
          <Field label="State" testId="pf-state"><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="bg-[#030712] border-slate-800" /></Field>
          <Field label="Volume (cars/month)" testId="pf-volume"><Input type="number" value={form.estimated_volume} onChange={(e) => setForm({ ...form, estimated_volume: e.target.value })} className="bg-[#030712] border-slate-800 font-mono" placeholder="20" /></Field>
          <Field label="Pipeline Value $" testId="pf-value"><Input type="number" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} className="bg-[#030712] border-slate-800 font-mono" placeholder="15000" /></Field>
          <Field label="Source" testId="pf-source" span2>
            <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}
              className="h-10 w-full px-3 rounded-md bg-[#030712] border border-slate-800 text-sm text-slate-200">
              <option value="cold_visit">Cold Visit</option>
              <option value="referral">Referral</option>
              <option value="website">Website</option>
              <option value="trade_show">Trade Show</option>
              <option value="other">Other</option>
            </select>
          </Field>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider font-bold text-slate-400">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="mt-1.5 bg-[#030712] border-slate-800 text-sm min-h-[60px]"
              placeholder="First impressions, decision maker, pain points..." />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" className="border-slate-700" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500" data-testid="pf-confirm">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}
            Create
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, testId, span2 }) {
  return (
    <div className={span2 ? "col-span-2" : ""} data-testid={testId}>
      <Label className="text-xs uppercase tracking-wider text-slate-400 font-bold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
